import {
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { useState } from "react";
import SearchBar from "../../../components/SearchBar";
import CommonBreadcrumb from "../../../components/CommonBreadcrumb";
import SwitchToggle from "../../../components/SwitchToggle";
import React from "react";
import AddDamageType from "../../../Modals/addDamageType";
import DeleteConfirmModal from "../../../Modals/DeleteConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import Can from "../../../components/Can";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { useGetDamageTypeQuery, useDeleteDamageTypeMutation, useUpdateDamageTypeMutation, useGetCategoriesQuery } from "../../../service/features/api";
import { VEHICLE_TYPE } from "../../../constants/enum";
import { getVehicleTypeLabel } from "../../../utils/vehicle";

const ManageDamageType = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Vehicle Specification", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = React.useState('default');
  const [vehicleType, setVehicleType] = useState<string>('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as string);
    setPage(1);
  };

  const handleVehicleTypeChange = (event: SelectChangeEvent) => {
    setVehicleType(event.target.value as string);
    setCategory('default');
    setPage(1);
  };

  const { data, isLoading } = useGetDamageTypeQuery({
    search: searchTerm,
    page: page,
    limit: rowsPerPage,
    categoryId: category === 'default' ? undefined : category,
    type: vehicleType || undefined,
  });

  const { data: categoriesData } = useGetCategoriesQuery({
    search: "",
    page: 1,
    limit: 100,
    type: vehicleType || undefined,
  });

  const [deleteDamageType] = useDeleteDamageTypeMutation();
  const [updateDamageType] = useUpdateDamageTypeMutation();

  const handleDelete = (id: string) => {
    setItemToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteDamageType(itemToDelete).unwrap();
        toast.success("Damage Type deleted successfully / تم حذف نوع الضرر بنجاح");
        setDeleteModalOpen(false);
        setItemToDelete(null);
      } catch (error: any) {
        console.error("Failed to delete damage type", error);
        toast.error(error?.data?.message || "Failed to delete damage type / فشل حذف نوع الضرر");
      }
    }
  };

  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setOpenModal(true);
  };

  const handleToggleStatus = async (row: any, currentStatus: boolean) => {
    const payload = {
      id: row._id,
      name: row.name,
      isActive: !currentStatus,
      categoryId: row.category?.map((c: any) => c._id) || (row.categoryId ? (Array.isArray(row.categoryId) ? row.categoryId : [row.categoryId]) : []),
      type: row.type || row.vehicleType
    };

    try {
      await updateDamageType(payload).unwrap();
      toast.success("Status updated successfully / تم تحديث الحالة بنجاح");
    } catch (error: any) {
      console.error("Failed to update status", error);
      toast.error(error?.data?.message || "Failed to update status / فشل تحديث الحالة");
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
  };

  return (
    <>
      <CommonBreadcrumb
        title="Damage Type Management / إدارة نوع الضرر"
        breadcrumbItems={[
          { label: "Dashboard / لوحة المعلومات", onClick: () => navigate("/dashboard") },
          { label: "Listed Vehicles / المركبات المدرجة", onClick: () => navigate("/manage-vehicles") },
          { label: "Damage Type Management / إدارة نوع الضرر" },
        ]}
      />

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Damage Types... / ابحث عن أنواع الضرر..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  value={vehicleType}
                  onChange={handleVehicleTypeChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Vehicle Type / اختر نوع المركبة</MenuItem>
                  {Object.values(VEHICLE_TYPE)
                    .filter((v) => typeof v === "number")
                    .map((type) => (
                      <MenuItem key={type} value={type.toString()}>
                        {getVehicleTypeLabel(type as number)}
                      </MenuItem>
                    ))}
                </Select>
              </div>
              <div className="control_group">
                <Select
                  fullWidth
                  value={category}
                  onChange={handleCategoryChange}
                >
                  <MenuItem disabled value="default">Select Category / اختر الفئة</MenuItem>
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <div className="right_s">
            <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedItem(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> Add Damage Type / إضافة نوع ضرر
              </Button>
            </Can>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No. / الرقم التسلسلي</TableCell>
                <TableCell>Damage Type Detail / تفاصيل نوع الضرر</TableCell>
                <TableCell>Categories / الفئات</TableCell>
                <TableCell>Created Date / تاريخ الإنشاء</TableCell>
                <TableCell>Disable/Enable / تعطيل/تفعيل</TableCell>
                <TableCell>Actions / الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">Loading... / جارٍ التحميل...</TableCell>
                </TableRow>
              ) : (
                data?.data?.data?.map((row: any, index: number) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <div className="user_block">
                        <p>
                          <b>{row.name || "--"}</b>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {(row.category || row.categories || row.categoryId)?.map((c: any) => c.name).join(", ") || (Array.isArray(row.categoryName) ? row.categoryName.join(", ") : row.categoryName) || "--"}
                    </TableCell>
                    <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={() => handleToggleStatus(row, row.isActive)}
                        />
                      ) : (
                        <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isActive ? "Enabled / مفعل" : "Disabled / معطل"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit / تعديل" arrow>
                            <IconButton
                              onClick={() => handleEdit(row)}
                            >
                              <img src="/static/images/edit2_icon.svg" alt="Edit / تعديل" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete / حذف" arrow>
                            <IconButton
                              onClick={() => handleDelete(row._id)}
                            >
                              <img src="/static/images/trash2_icon.svg" alt="Delete / حذف" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                )))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <AddDamageType
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        selectedItem={selectedItem}
      />

      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Damage Type / حذف نوع الضرر"
        message="Are you sure you want to delete this damage type? / هل أنت متأكد أنك تريد حذف نوع الضرر هذا؟"
      />
    </>
  );
};

export default ManageDamageType;
